import { useEffect, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchHostWorkspace } from '@/api/hostWorkspace/hostWorkspaceApi';
import { FullScreenLoader } from '@/components/common/FullScreenLoader';
import { DomainWorkspaceMismatchScreen } from '@/components/auth/DomainWorkspaceMismatchScreen';
import { HOST_WORKSPACE_QUERY_KEY } from '@/constants/queryKeys';
import {
  bindHostWorkspaceTenant,
  DOMAIN_WORKSPACE_MISMATCH_EVENT_NAME,
} from '@/lib/hostWorkspaceBinding';
import { HostWorkspaceContext } from './hostWorkspaceContext';

interface HostWorkspaceProviderProps {
  children: ReactNode;
}

async function loadAndBindHostWorkspace() {
  const hostWorkspace = await fetchHostWorkspace();
  bindHostWorkspaceTenant(hostWorkspace === null ? '' : hostWorkspace.tenantId);
  return hostWorkspace;
}

export function HostWorkspaceProvider({ children }: HostWorkspaceProviderProps) {
  const [isMismatched, setIsMismatched] = useState(false);
  const hostWorkspaceQuery = useQuery({
    queryKey: HOST_WORKSPACE_QUERY_KEY,
    queryFn: loadAndBindHostWorkspace,
    staleTime: Infinity,
    retry: false,
  });

  useEffect(function listenForDomainWorkspaceMismatch() {
    function markMismatched() {
      setIsMismatched(true);
    }
    window.addEventListener(DOMAIN_WORKSPACE_MISMATCH_EVENT_NAME, markMismatched);
    return function stopListening() {
      window.removeEventListener(DOMAIN_WORKSPACE_MISMATCH_EVENT_NAME, markMismatched);
    };
  }, []);

  if (hostWorkspaceQuery.isPending) {
    return <FullScreenLoader />;
  }
  const hostWorkspace = hostWorkspaceQuery.data ?? null;
  if (hostWorkspace !== null && isMismatched) {
    return <DomainWorkspaceMismatchScreen hostWorkspace={hostWorkspace} />;
  }
  return (
    <HostWorkspaceContext.Provider value={{ hostWorkspace }}>
      {children}
    </HostWorkspaceContext.Provider>
  );
}
